import CloseIcon from "@mui/icons-material/CloseRounded";
import DragIndicatorIcon from "@mui/icons-material/DragIndicatorRounded";
import { Box, IconButton, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import HiddenIcon from "@entrilia/shared/icons/HiddenIcon";
import VisibleIcon from "@entrilia/shared/icons/VisibleIcon";
import FieldIcon from "../../../common/domain-icons/FieldIcon";
import FieldSourceBadge from "../../../entityFields/FieldSourceBadge";
import ObjectLayoutContext from "./ObjectLayoutContext";
import type { LayoutField } from "./objectLayoutState";
import { removeFieldAction, toggleFieldVisibilityAction } from "./objectLayoutState";

interface Props {
  field: LayoutField;
  disableActions?: boolean;
}

const LayoutFieldItem = ({ field, disableActions }: Props) => {
  const { setLayoutState } = useDefinedContext(ObjectLayoutContext);
  const { sendNotification } = useNotificationContext();

  const handleToggleVisibility = () => {
    setLayoutState(toggleFieldVisibilityAction(field.id));
  };

  const handleRemove = () => {
    setLayoutState(removeFieldAction(field.id));
    sendNotification("Field removed from layout");
  };

  return (
    <ListItem
      draggable
      sx={{ px: 0.8 }}
      dense
      secondaryAction={
        <>
          <Box component="span" sx={{ pr: 1 }}>
            <FieldSourceBadge fieldSource={field.source} />
          </Box>
          <IconButton edge="end" sx={{ ml: 1 }} onClick={handleToggleVisibility} disabled={disableActions}>
            {field.isVisible ? <VisibleIcon /> : <HiddenIcon />}
          </IconButton>
          <IconButton edge="end" sx={{ ml: 1 }} onClick={handleRemove} disabled={disableActions}>
            <CloseIcon />
          </IconButton>
        </>
      }
    >
      <ListItemIcon sx={{ pl: 0 }}>
        <DragIndicatorIcon sx={{ cursor: "grab", mr: 0.5 }} />
        <FieldIcon fieldType={field.type} color="action" />
      </ListItemIcon>
      <ListItemText sx={{ flexGrow: 1, ml: 1.5 }}>
        <TypographyTooltipEllipsis text={field.name} typographyProps={{ component: "span", sx: { pr: 4 } }} />
      </ListItemText>
    </ListItem>
  );
};

export default LayoutFieldItem;
